import React, { useState, useEffect } from "react";
import { Modal, Form, Select, DatePicker, Input, message } from "antd";
import { useSelector } from "react-redux";
import { incidentService, propertyService } from "@iso/services";
import moment from "moment";

const { Option } = Select;
const { TextArea } = Input;

const ReallocateModal = ({ visible, action, incident, onCancel, onSaved }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [propertyUsers, setPropertyUsers] = useState([]);
  const activePropertyId = useSelector((state) => state.property.activePropertyId);
  const propertyId = incident?.property_id || activePropertyId;

  useEffect(() => {
    if (visible && propertyId) {
      propertyService.getListUsersOfProperty(propertyId, true).then((res) => {
        if (res && res.users) {
          setPropertyUsers(res.users);
        }
      });
    }
  }, [visible, propertyId]);

  useEffect(() => {
    if (visible && action) {
      form.setFieldsValue({
        assigned_to: action.assigned_to || undefined,
        due_date: action.due_date ? moment(action.due_date) : undefined,
        reason: undefined,
      });
    } else {
      form.resetFields();
    }
  }, [visible, action, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      await incidentService.reallocateCorrectiveAction(action.id, {
        assigned_to: values.assigned_to,
        due_date: values.due_date.format("YYYY-MM-DD"),
        reason: values.reason,
      });

      message.success("Corrective action re-allocated successfully.");
      onSaved && onSaved();
      onCancel();
    } catch (err) {
      if (err.errorFields) return;
      message.error(
        err?.response?.data?.message || "Failed to re-allocate corrective action."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={visible}
      title="Re-allocate Corrective Action"
      onCancel={onCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      destroyOnClose
      okText="Re-allocate"
      okButtonProps={{ style: { backgroundColor: "#0B2A1B", borderColor: "#0B2A1B" } }}
    >
      {action && (
        <p style={{ color: "#667085", marginBottom: 16, fontSize: 13 }}>
          <strong>Action:</strong> {action.description}
        </p>
      )}

      <Form form={form} layout="vertical">
        <Form.Item
          name="assigned_to"
          label="Assign To"
          rules={[{ required: true, message: "Please select a person to assign this action to" }]}
        >
          <Select
            showSearch
            placeholder="Select staff member"
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
            }
          >
            {propertyUsers.map((user) => (
              <Option key={user.id} value={user.id}>
                {user.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="due_date"
          label="Due Date"
          rules={[{ required: true, message: "Due date is required" }]}
        >
          <DatePicker
            style={{ width: "100%" }}
            format="DD/MM/YYYY"
            disabledDate={(current) => current && current < moment().startOf("day")}
          />
        </Form.Item>

        <Form.Item
          name="reason"
          label="Reason for Re-allocation"
          rules={[{ required: true, whitespace: true, message: "Please provide a reason for the re-allocation" }]}
        >
          <TextArea
            rows={3}
            placeholder="Enter the reason for re-allocating this action..."
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ReallocateModal;
